// Bridge between the live collab client (usePresence) and the non-React
// CustomAssetManager (ENG-539 Stage 2). It lets the asset manager ask "is
// co-editing live?" and route a material_mapping edit into the shared Yjs doc,
// and lets it receive remote mapping changes — without either side importing
// the other. A module singleton because the collab client is owned by a React
// hook while the asset manager is a plain module.

import { dedupBySlot } from './collabMapping.js'

let _client = null       // the connected CollabPresenceClient (or null)
let _unsub = null        // active observeMapping unsubscribe
let _onRemote = null     // handler set by CustomAssetManager

function rewire() {
  if (_unsub) { _unsub(); _unsub = null }
  if (_client && _onRemote) {
    // Collapse same-slot dupes on read (per-row-replace CRDT artifact).
    _unsub = _client.observeMapping((rows) => _onRemote(dedupBySlot(rows)))
  }
}

// registerCollabClient — called by usePresence once connected. Pass null-safe.
export function registerCollabClient(client) {
  _client = client
  rewire()
}

export function unregisterCollabClient(client) {
  if (client && client !== _client) return
  if (_unsub) { _unsub(); _unsub = null }
  _client = null
}

// setRemoteMappingHandler — CustomAssetManager registers how to apply a remote
// mapping (update in-memory state + reload the avatar with that mapping).
export function setRemoteMappingHandler(cb) {
  _onRemote = cb
  rewire()
}

// isCollabActive — true only when a client is connected AND synced, so a
// caller can safely route an edit through the doc (else write straight to AAM).
export function isCollabActive() {
  return !!(_client && _client.isSynced())
}

// writeMappingCollab — route an edit into the shared doc. Returns false when
// co-editing isn't live, so the caller falls back to a direct AAM write.
export function writeMappingCollab(rows) {
  if (!isCollabActive()) return false
  _client.writeMapping(rows)
  return true
}

// currentCollabMapping — the live converged mapping when co-editing, else null.
// The avatar reload prefers this over an AAM re-fetch while co-editing, since
// the collab service persists to AAM only debounced (a re-fetch would be stale).
export function currentCollabMapping() {
  if (!isCollabActive()) return null
  return dedupBySlot(_client.readMapping())
}
